import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowLeft,
  ClipboardCheck,
  DollarSign,
  Eye,
  TrendingUp,
  Workflow,
} from 'lucide-react';
import ValuationSnapshotExpertView from './SnapshotViews/ValuationSnapshotExpertView';
import FinancingSnapshotExpertView from './SnapshotViews/FinancingSnapshotExpertView';
import DealStructureSnapshotExpertView from './SnapshotViews/DealStructureSnapshotExpertView';
import AssessmentSnapshotExpertView from './SnapshotViews/AssessmentSnapshotExpertView';
import OwnerViewModal from './OwnerViewModal';

// Snapshot types that have an owner-facing rich component to drop into the
// "Show owner's view" modal. assessment_snapshot is intentionally absent —
// the assessment surface is a questionnaire, not a results dashboard, so
// there's no equivalent owner view to render. The trigger button is hidden
// for that type.
const OWNER_VIEW_SUPPORTED_TYPES = new Set([
  'valuation_snapshot',
  'financing_snapshot',
  'deal_structure_snapshot',
]);

const SNAPSHOT_TYPE_CONFIG = {
  valuation_snapshot: { title: 'Valuation Snapshot', icon: TrendingUp },
  financing_snapshot: { title: 'Financing Scenario', icon: DollarSign },
  deal_structure_snapshot: { title: 'Deal Structure', icon: Workflow },
  assessment_snapshot: { title: 'Readiness Assessment', icon: ClipboardCheck },
};

const EXPERT_VIEW_BY_TYPE = {
  valuation_snapshot: ValuationSnapshotExpertView,
  financing_snapshot: FinancingSnapshotExpertView,
  deal_structure_snapshot: DealStructureSnapshotExpertView,
  assessment_snapshot: AssessmentSnapshotExpertView,
};

const ROLE_LABEL = {
  cbv: 'CBV',
  business_broker: 'BROKER',
  lawyer: 'LAWYER',
  accountant: 'ACCOUNTANT',
  lender: 'LENDER',
  cepa_coach: 'CEPA',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return '';
  }
}

const RoleBadges = ({ roles }) => {
  if (!roles || roles.length === 0) {
    return (
      <span className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/50 px-1.5 py-0.5 rounded">
        ALL TEAM
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {roles.map((role) => (
        <span
          key={role}
          className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-1.5 py-0.5 rounded">
          {ROLE_LABEL[role] || role}
        </span>
      ))}
    </span>
  );
};

RoleBadges.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
};

RoleBadges.defaultProps = {
  roles: [],
};

const DealTeamSnapshotView = ({
  dealTeamId,
  vaultDocumentId,
  snapshotType,
  sharedAt,
  sharedByName,
  teamName,
  note,
  sharedWithRoles,
  snapshot,
}) => {
  const [ownerViewOpen, setOwnerViewOpen] = useState(false);

  useEffect(() => {
    if (window.ahoy) {
      window.ahoy.track('succeedo_deal_team_snapshot_viewed', {
        deal_team_id: dealTeamId,
        vault_document_id: vaultDocumentId,
        source_type: snapshotType,
      });
    }
  }, [dealTeamId, vaultDocumentId, snapshotType]);

  const cfg = SNAPSHOT_TYPE_CONFIG[snapshotType] || {};
  const Icon = cfg.icon || TrendingUp;
  const ExpertView = EXPERT_VIEW_BY_TYPE[snapshotType];
  const hasOwnerView = OWNER_VIEW_SUPPORTED_TYPES.has(snapshotType);

  const handleOpenOwnerView = () => {
    setOwnerViewOpen(true);
    if (window.ahoy) {
      window.ahoy.track('succeedo_deal_team_snapshot_owner_view_opened', {
        deal_team_id: dealTeamId,
        vault_document_id: vaultDocumentId,
        source_type: snapshotType,
      });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      {/* Back link */}
      <a
        href={`/succeedo/teams/${dealTeamId}/workspace`}
        className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to {teamName || 'workspace'}
      </a>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-[hsl(var(--succeedo-orange))]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
            {snapshot?.label || cfg.title || 'Snapshot'}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-1.5 py-0.5 rounded">
              READ-ONLY
            </span>
            <RoleBadges roles={sharedWithRoles} />
          </div>
        </div>
        {/* "Show owner's view" affordance — opens a modal hosting the
            owner-facing component for this snapshot type in read-only mode.
            Hidden for assessment_snapshot (no equivalent owner dashboard). */}
        {hasOwnerView && (
          <button
            type="button"
            onClick={handleOpenOwnerView}
            className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wide rounded border border-[hsl(var(--border))] hover:border-[hsl(var(--succeedo-orange))]/40 hover:text-[hsl(var(--succeedo-orange))] text-[hsl(var(--muted-foreground))] px-3 py-1.5 transition-colors flex-shrink-0"
            aria-label="Show what the owner sees in their calculator"
          >
            <Eye className="w-3.5 h-3.5" />
            Show owner&rsquo;s view
          </button>
        )}
      </div>

      {/* Shared-by banner */}
      <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4 space-y-2">
        <div className="text-[13px] text-[hsl(var(--foreground))]">
          Shared by{' '}
          <span className="font-medium">{sharedByName || 'Team owner'}</span>{' '}
          with the <span className="font-medium">{teamName}</span> team on{' '}
          <span className="font-medium">{formatDate(sharedAt)}</span>.
        </div>
        {note && (
          <div className="text-[13px] italic text-[hsl(var(--muted-foreground))] border-l-2 border-[hsl(var(--succeedo-orange))]/40 pl-3">
            &ldquo;{note}&rdquo;
          </div>
        )}
      </div>

      {/* Expert view (dispatched by snapshot type) */}
      {ExpertView ? (
        <ExpertView snapshot={snapshot || {}} />
      ) : (
        <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
          Unknown snapshot type: {snapshotType}
        </p>
      )}

      {/* Footer */}
      <div className="text-[11px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] pt-4 border-t border-[hsl(var(--border))]">
        Snapshot ID {snapshot?.id || '—'} · No edit controls · Frozen at last share
      </div>

      {/* Owner-view modal (rendered at the page level so the backdrop covers
          everything). Always mounted; visibility gated on `open`. */}
      <OwnerViewModal
        open={ownerViewOpen}
        onClose={() => setOwnerViewOpen(false)}
        snapshot={snapshot}
        snapshotType={snapshotType}
      />
    </div>
  );
};

DealTeamSnapshotView.propTypes = {
  dealTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vaultDocumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  snapshotType: PropTypes.string.isRequired,
  sharedAt: PropTypes.string,
  sharedByName: PropTypes.string,
  teamName: PropTypes.string,
  note: PropTypes.string,
  sharedWithRoles: PropTypes.arrayOf(PropTypes.string),
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    preview_metrics: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
    }),
    inputs: PropTypes.object,
    results: PropTypes.object,
    payload: PropTypes.object,
  }),
};

DealTeamSnapshotView.defaultProps = {
  sharedAt: null,
  sharedByName: null,
  teamName: null,
  note: null,
  sharedWithRoles: [],
  snapshot: null,
};

export default DealTeamSnapshotView;
